import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ColumnTable, Icon } from '@octano/global-ui';
import { format as formatRut, validate as validateRut } from 'rut.js';

import { TranslationsKeys } from '../../../../locales/translations';
import { HomologationRequestListItem } from '../types';
import HomologationStatusBadge from '../parts/HomologationStatusBadge';

export default function useColumns({
  onViewDetail,
  onShowObservation,
}: {
  onViewDetail: (row: HomologationRequestListItem) => void;
  onShowObservation: (row: HomologationRequestListItem) => void;
}): ColumnTable<HomologationRequestListItem>[] {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION_AUTHORIZATION);

  return useMemo(
    () => [
      {
        columnName: 'studentName',
        headerText: t('table.studentName'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => (
          <div>
            <div className="fw-bold">{row.studentName}</div>
            <small className="text-muted">
              {row.studentRut
                ? validateRut(row.studentRut)
                  ? formatRut(row.studentRut)
                  : row.studentRut
                : '-'}
            </small>
          </div>
        ),
      },
      {
        columnName: 'createdAt',
        headerText: t('table.createdAt'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <span>{new Date(row.createdAt).toLocaleDateString('es-CL')}</span>
        ),
      },
      {
        columnName: 'originInstitution',
        headerText: t('table.originInstitution'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => <span>{row.originInstitution || '-'}</span>,
      },
      {
        columnName: 'originProgramName',
        headerText: t('table.originProgram'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => <span>{row.originProgramName || '-'}</span>,
      },
      {
        columnName: 'programName',
        headerText: t('table.program'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => <span>{row.programName || '-'}</span>,
      },
      {
        columnName: 'studentEmail',
        headerText: t('table.email'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => <span>{row.studentEmail || '-'}</span>,
      },
      {
        columnName: 'studentPhone',
        headerText: t('table.phone'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => <span>{row.studentPhone || '-'}</span>,
      },
      {
        columnName: 'status',
        headerText: t('table.status'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <HomologationStatusBadge
            resolution={row.resolution}
            translationKey={TranslationsKeys.HOMOLOGATION_AUTHORIZATION}
          />
        ),
      },
      {
        columnName: 'observation',
        headerText: t('table.observation'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.observation ? (
            <span
              role="button"
              style={{ cursor: 'pointer', color: '#2266CC' }}
              onClick={() => onShowObservation(row)}
              title={t('table.viewObservation')}
            >
              <Icon name="information" size="20px" color="primary" />
            </span>
          ) : (
            <span>-</span>
          ),
      },
      {
        columnName: 'actions',
        headerText: t('table.action'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const isAuthorized = row.resolution !== 'pending';
          return (
            <Button
              text={isAuthorized ? t('table.view') : t('table.authorize')}
              size="sm"
              onClick={() => onViewDetail(row)}
              style={{ minWidth: 120 }}
            />
          );
        },
      },
    ],
    [t, onViewDetail, onShowObservation],
  );
}
